/**
 * Headphone Profile Database for HearClear calibration.
 * 
 * Each profile contains:
 *   sensitivity_db_mw: dB SPL per 1 mW (from specs or lab measurements)
 *   impedance: nominal impedance in ohms
 *   freq_correction: per-frequency offset relative to 1kHz response (dB)
 *                    positive = louder than 1kHz, negative = quieter
 *   max_spl: maximum output SPL (safety ceiling)
 * 
 * Match keys are LOWERCASE fragments of the Bluetooth device label
 * returned by navigator.mediaDevices.enumerateDevices().
 */

// ─── Known Headphone Profiles ──────────────────────────────────────────────
export const HEADPHONE_PROFILES = {
  // ═══ Apple ═══
  airpods_pro_3: {
    name: "AirPods Pro 3", brand: "Apple",
    sensitivity_db_mw: 105, impedance: 16, max_spl: 110,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: -1, 4000: -2, 8000: -4 },
  },
  airpods_pro_2: {
    name: "AirPods Pro 2", brand: "Apple",
    sensitivity_db_mw: 105, impedance: 16, max_spl: 110,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: -1, 4000: -2, 8000: -4 },
  },
  airpods_pro: {
    name: "AirPods Pro", brand: "Apple",
    sensitivity_db_mw: 104, impedance: 16, max_spl: 110,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: -1, 4000: -3, 8000: -5 },
  },
  airpods_4: {
    name: "AirPods 4", brand: "Apple",
    sensitivity_db_mw: 102, impedance: 16, max_spl: 107,
    freq_correction: { 250: 2, 500: 0, 1000: 0, 2000: 0, 4000: -2, 8000: -4 },
  },
  airpods_3: {
    name: "AirPods 3rd Gen", brand: "Apple",
    sensitivity_db_mw: 102, impedance: 16, max_spl: 107,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -2, 8000: -4 },
  },
  airpods_2: {
    name: "AirPods 2nd Gen", brand: "Apple",
    sensitivity_db_mw: 100, impedance: 16, max_spl: 105,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -3, 8000: -5 },
  },
  airpods_max: {
    name: "AirPods Max", brand: "Apple",
    sensitivity_db_mw: 100, impedance: 16, max_spl: 105,
    freq_correction: { 250: 0, 500: 0, 1000: 0, 2000: 0, 4000: -1, 8000: -3 },
  },
  earpods: {
    name: "EarPods", brand: "Apple",
    sensitivity_db_mw: 106, impedance: 32, max_spl: 112,
    freq_correction: { 250: 3, 500: 1, 1000: 0, 2000: -1, 4000: -4, 8000: -6 },
  },

  // ═══ Samsung ═══
  galaxy_buds3_pro: {
    name: "Galaxy Buds3 Pro", brand: "Samsung",
    sensitivity_db_mw: 103, impedance: 16, max_spl: 108,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -1, 8000: -3 },
  },
  galaxy_buds2_pro: {
    name: "Galaxy Buds2 Pro", brand: "Samsung",
    sensitivity_db_mw: 102, impedance: 16, max_spl: 107,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: -1, 4000: -2, 8000: -4 },
  },
  galaxy_buds_fe: {
    name: "Galaxy Buds FE", brand: "Samsung",
    sensitivity_db_mw: 100, impedance: 16, max_spl: 105,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -2, 8000: -5 },
  },
  galaxy_buds_live: {
    name: "Galaxy Buds Live", brand: "Samsung",
    sensitivity_db_mw: 99, impedance: 16, max_spl: 104,
    freq_correction: { 250: 3, 500: 1, 1000: 0, 2000: 0, 4000: -3, 8000: -5 },
  },

  // ═══ Sony ═══
  sony_wf1000xm5: {
    name: "WF-1000XM5", brand: "Sony",
    sensitivity_db_mw: 106, impedance: 16, max_spl: 112,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: 0, 4000: -1, 8000: -3 },
  },
  sony_wf1000xm4: {
    name: "WF-1000XM4", brand: "Sony",
    sensitivity_db_mw: 105, impedance: 16, max_spl: 111,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: 0, 4000: -2, 8000: -4 },
  },
  sony_whch720n: {
    name: "WH-CH720N", brand: "Sony",
    sensitivity_db_mw: 100, impedance: 32, max_spl: 105,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: 0, 4000: -1, 8000: -3 },
  },
  sony_wh1000xm5: {
    name: "WH-1000XM5", brand: "Sony",
    sensitivity_db_mw: 102, impedance: 48, max_spl: 107,
    freq_correction: { 250: 0, 500: 0, 1000: 0, 2000: 0, 4000: -1, 8000: -2 },
  },

  // ═══ JBL ═══
  jbl_tune230nc: {
    name: "Tune 230NC", brand: "JBL",
    sensitivity_db_mw: 102, impedance: 16, max_spl: 107,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -2, 8000: -4 },
  },
  jbl_tune125tws: {
    name: "Tune 125TWS", brand: "JBL",
    sensitivity_db_mw: 100, impedance: 16, max_spl: 105,
    freq_correction: { 250: 3, 500: 1, 1000: 0, 2000: 0, 4000: -3, 8000: -5 },
  },
  jbl_live_pro2: {
    name: "Live Pro 2", brand: "JBL",
    sensitivity_db_mw: 103, impedance: 16, max_spl: 108,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: 0, 4000: -1, 8000: -3 },
  },

  // ═══ boAt (India #1) ═══
  boat_airdopes_plus: {
    name: "boAt Airdopes Plus", brand: "boAt",
    sensitivity_db_mw: 112, impedance: 16, max_spl: 115,
    freq_correction: { 250: 3, 500: 2, 1000: 0, 2000: -1, 4000: -3, 8000: -6 },
  },
  boat_airdopes_generic: {
    name: "boAt Airdopes", brand: "boAt",
    sensitivity_db_mw: 106, impedance: 16, max_spl: 112,
    freq_correction: { 250: 3, 500: 2, 1000: 0, 2000: -1, 4000: -3, 8000: -6 },
  },
  boat_rockerz: {
    name: "boAt Rockerz", brand: "boAt",
    sensitivity_db_mw: 104, impedance: 32, max_spl: 110,
    freq_correction: { 250: 3, 500: 2, 1000: 0, 2000: -1, 4000: -3, 8000: -5 },
  },

  // ═══ Noise (India #2) ═══
  noise_buds: {
    name: "Noise Buds", brand: "Noise",
    sensitivity_db_mw: 104, impedance: 16, max_spl: 109,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -3, 8000: -5 },
  },

  // ═══ OnePlus ═══
  oneplus_buds_pro: {
    name: "OnePlus Buds Pro", brand: "OnePlus",
    sensitivity_db_mw: 103, impedance: 16, max_spl: 108,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: 0, 4000: -2, 8000: -4 },
  },
  oneplus_buds: {
    name: "OnePlus Buds", brand: "OnePlus",
    sensitivity_db_mw: 101, impedance: 16, max_spl: 106,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -2, 8000: -4 },
  },

  // ═══ Realme ═══
  realme_buds: {
    name: "Realme Buds", brand: "Realme",
    sensitivity_db_mw: 103, impedance: 16, max_spl: 108,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -2, 8000: -5 },
  },

  // ═══ Sennheiser ═══
  sennheiser_momentum: {
    name: "Momentum TWS", brand: "Sennheiser",
    sensitivity_db_mw: 107, impedance: 16, max_spl: 112,
    freq_correction: { 250: 0, 500: 0, 1000: 0, 2000: 0, 4000: -1, 8000: -2 },
  },
  sennheiser_hd200_pro: {
    name: "Sennheiser HD 200 Pro", brand: "Sennheiser",
    sensitivity_db_mw: 108, impedance: 32, max_spl: 114,
    freq_correction: { 250: 0, 500: 0, 1000: 0, 2000: 0, 4000: -1, 8000: -2 },
    wired: true, // Must be manually selected (not auto-detected via Bluetooth)
  },
  sennheiser_hd206: {
    name: "Sennheiser HD 206", brand: "Sennheiser",
    sensitivity_db_mw: 108, impedance: 24, max_spl: 114,
    freq_correction: { 250: 1, 500: 0, 1000: 0, 2000: 0, 4000: -1, 8000: -3 },
    wired: true,
  },

  // ═══ Nothing ═══
  nothing_ear: {
    name: "Nothing Ear", brand: "Nothing",
    sensitivity_db_mw: 102, impedance: 16, max_spl: 107,
    freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -2, 8000: -4 },
  },

  // ═══ Clinical (Wired Only) ═══
  radioear_ip30: {
    name: "RadioEar IP30", brand: "RadioEar",
    sensitivity_db_mw: 103, impedance: 50, max_spl: 120,
    freq_correction: { 250: 0, 500: 0, 1000: 0, 2000: 0, 4000: 0, 8000: -1 },
    wired: true,
    clinical_grade: true,
  },
};

// ─── Matching Rules ────────────────────────────────────────────────────────
// Each rule: [substring to match in device label (lowercase), profile key]
// Order matters — more specific matches first
export const HEADPHONE_MATCH_RULES = [
  // Apple
  ["airpods pro 3", "airpods_pro_3"],
  ["airpods pro 2", "airpods_pro_2"],
  ["airpods pro", "airpods_pro"],  // catches any AirPods Pro not matched above
  ["airpods max", "airpods_max"],
  ["airpods 4", "airpods_4"],
  ["airpods 3", "airpods_3"],
  ["airpods 2", "airpods_2"],
  ["airpods", "airpods_3"],  // fallback for unversioned AirPods → use gen 3 profile
  ["earpods", "earpods"],
  // Samsung
  ["buds3 pro", "galaxy_buds3_pro"],
  ["buds 3 pro", "galaxy_buds3_pro"],
  ["buds2 pro", "galaxy_buds2_pro"],
  ["buds 2 pro", "galaxy_buds2_pro"],
  ["buds fe", "galaxy_buds_fe"],
  ["buds live", "galaxy_buds_live"],
  ["galaxy buds", "galaxy_buds2_pro"],  // fallback Samsung buds
  // Sony
  ["wf-1000xm5", "sony_wf1000xm5"],
  ["wf-1000xm4", "sony_wf1000xm4"],
  ["wh-1000xm5", "sony_wh1000xm5"],
  ["wh-ch720n", "sony_whch720n"],
  ["sony wf", "sony_wf1000xm5"],  // fallback Sony TWS
  ["sony wh", "sony_wh1000xm5"],  // fallback Sony over-ear
  // JBL
  ["tune 230", "jbl_tune230nc"],
  ["tune230", "jbl_tune230nc"],
  ["tune 125", "jbl_tune125tws"],
  ["live pro", "jbl_live_pro2"],
  ["jbl", "jbl_tune230nc"],  // fallback JBL
  // boAt
  ["airdopes plus", "boat_airdopes_plus"],
  ["airdopes", "boat_airdopes_generic"],
  ["rockerz", "boat_rockerz"],
  ["boat", "boat_airdopes_generic"],  // fallback boAt
  // Noise
  ["noise", "noise_buds"],
  // OnePlus
  ["oneplus buds pro", "oneplus_buds_pro"],
  ["oneplus buds", "oneplus_buds"],
  ["oneplus", "oneplus_buds"],
  // Realme
  ["realme", "realme_buds"],
  // Sennheiser
  ["hd 200 pro", "sennheiser_hd200_pro"],
  ["hd200 pro", "sennheiser_hd200_pro"],
  ["hd 206", "sennheiser_hd206"],
  ["hd206", "sennheiser_hd206"],
  ["momentum", "sennheiser_momentum"],
  ["sennheiser", "sennheiser_momentum"],
  // Nothing
  ["nothing ear", "nothing_ear"],
  ["nothing", "nothing_ear"],
];

// ─── Standard Range Profile (Tier 3 fallback) ─────────────────────────────
// Based on median of popular consumer TWS earbuds in India
export const STANDARD_RANGE_PROFILE = {
  name: "Standard Range (Consumer TWS Average)",
  brand: "Generic",
  sensitivity_db_mw: 104, // median of popular models
  impedance: 16,
  max_spl: 109,
  freq_correction: { 250: 2, 500: 1, 1000: 0, 2000: 0, 4000: -2, 8000: -5 },
};

// ─── Phone DAC Output Estimates (Vrms at given volume %) ───────────────────
// These are approximate values based on available measurements.
// Key: volume percentage → output Vrms
export const PHONE_DAC_PROFILES = {
  iphone: {
    name: "iPhone",
    // iPhone typical: ~1.0V max output
    output_vrms: { 50: 0.35, 60: 0.45, 70: 0.55, 80: 0.70, 90: 0.85, 100: 1.0 },
    has_db_meter: true,
    db_meter_instructions: "control_center",
  },
  samsung: {
    name: "Samsung Galaxy",
    // Samsung typical: ~0.7V max output
    output_vrms: { 50: 0.25, 60: 0.32, 70: 0.40, 80: 0.50, 90: 0.60, 100: 0.70 },
    has_db_meter: true,
    db_meter_instructions: "digital_wellbeing",
  },
  // Phones without built-in dB meters — use standard estimates
  vivo: {
    name: "Vivo",
    output_vrms: { 50: 0.25, 60: 0.32, 70: 0.40, 80: 0.50, 90: 0.60, 100: 0.70 },
    has_db_meter: false,
  },
  xiaomi: {
    name: "Xiaomi/Redmi",
    output_vrms: { 50: 0.25, 60: 0.32, 70: 0.40, 80: 0.50, 90: 0.60, 100: 0.70 },
    has_db_meter: false,
  },
  oppo: {
    name: "Oppo",
    output_vrms: { 50: 0.25, 60: 0.32, 70: 0.40, 80: 0.50, 90: 0.60, 100: 0.70 },
    has_db_meter: false,
  },
  oneplus: {
    name: "OnePlus",
    output_vrms: { 50: 0.28, 60: 0.35, 70: 0.45, 80: 0.55, 90: 0.65, 100: 0.75 },
    has_db_meter: false,
  },
  pixel: {
    name: "Google Pixel",
    output_vrms: { 50: 0.20, 60: 0.26, 70: 0.33, 80: 0.38, 90: 0.40, 100: 0.43 },
    has_db_meter: false,
  },
  generic: {
    name: "Unknown Device",
    // Conservative average for Android phones
    output_vrms: { 50: 0.25, 60: 0.32, 70: 0.40, 80: 0.50, 90: 0.60, 100: 0.70 },
    has_db_meter: false,
  },
};

/**
 * Get all wired headphone profiles for manual selection dropdown.
 * (Wired headphones can't be auto-detected via Bluetooth enumerateDevices)
 * @returns {Array<{key: string, name: string, brand: string, clinical_grade: boolean}>}
 */
export function getWiredHeadphoneOptions() {
  return Object.entries(HEADPHONE_PROFILES)
    .filter(([, p]) => p.wired)
    .map(([key, p]) => ({
      key,
      name: p.name,
      brand: p.brand,
      clinical_grade: !!p.clinical_grade,
    }));
}

/**
 * Match a Bluetooth audio device label to a headphone profile.
 * @param {string} label — device label from enumerateDevices()
 * @returns {{ profile: object, key: string } | null}
 */
export function matchHeadphoneProfile(label) {
  if (!label) return null;
  const lower = label.toLowerCase();
  for (const [substring, profileKey] of HEADPHONE_MATCH_RULES) {
    if (lower.includes(substring)) {
      const profile = HEADPHONE_PROFILES[profileKey];
      if (profile) return { profile, key: profileKey };
    }
  }
  return null;
}

/**
 * Detect phone brand from User-Agent string.
 * @returns {string} — key into PHONE_DAC_PROFILES
 */
export function detectPhoneBrand() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (ua.includes("iphone") || ua.includes("ipad")) return "iphone";
  if (ua.includes("samsung") || ua.includes("sm-")) return "samsung";
  if (ua.includes("vivo")) return "vivo";
  if (ua.includes("xiaomi") || ua.includes("redmi") || ua.includes("poco")) return "xiaomi";
  if (ua.includes("oppo") || ua.includes("cph")) return "oppo";
  if (ua.includes("oneplus")) return "oneplus";
  if (ua.includes("pixel")) return "pixel";
  if (ua.includes("realme") || ua.includes("rmx")) return "generic";
  if (ua.includes("nothing")) return "generic";
  return "generic";
}

/**
 * Detect connected audio output devices (headphones).
 * Requires prior getUserMedia({audio:true}) permission.
 * @returns {Promise<Array<{label: string, deviceId: string, profile: object|null}>>}
 */
export async function detectAudioOutputDevices() {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter(d => d.kind === "audiooutput" && d.label)
      .map(d => {
        const match = matchHeadphoneProfile(d.label);
        return {
          label: d.label,
          deviceId: d.deviceId,
          profile: match?.profile || null,
          profileKey: match?.key || null,
        };
      });
  } catch (e) {
    console.warn("Failed to enumerate audio devices:", e);
    return [];
  }
}

/**
 * Calculate the Web Audio API gain needed for a target dB SPL,
 * given a known calibration point (reference SPL at a reference gain).
 * 
 * Used for Tier 1 (dB meter) calibration.
 * 
 * @param {number} targetSPL — desired SPL in dB
 * @param {number} refSPL — measured SPL at the reference gain
 * @param {number} refGain — the digital gain used during measurement
 * @returns {number} — Web Audio API gain value
 */
export function gainFromDbMeterCalibration(targetSPL, refSPL, refGain) {
  // dB difference between target and reference
  const dbDiff = targetSPL - refSPL;
  // Linear gain scaling
  return refGain * Math.pow(10, dbDiff / 20);
}

/**
 * Calculate the Web Audio API gain for a target dB HL level
 * using a headphone profile + phone DAC estimate.
 * 
 * Used for Tier 2 (headphone profile) and Tier 3 (standard range).
 * 
 * @param {number} dbHL — target hearing level in dB HL
 * @param {number} frequency — tone frequency in Hz
 * @param {object} headphoneProfile — from HEADPHONE_PROFILES or STANDARD_RANGE_PROFILE
 * @param {number} dacVrms — estimated DAC output voltage in Vrms
 * @param {object} retspl — RETSPL table
 * @returns {number} — Web Audio API gain value (0.0 to 1.0)
 */
export function gainFromHeadphoneProfile(dbHL, frequency, headphoneProfile, dacVrms, retspl) {
  const { sensitivity_db_mw, impedance, freq_correction, max_spl } = headphoneProfile;
  
  // Target SPL = dB HL + RETSPL at this frequency
  const retsplVal = retspl[frequency] ?? retspl[1000] ?? 5.5;
  const targetSPL = dbHL + retsplVal;
  
  // Headphone frequency correction (relative to 1kHz)
  const freqCorr = (freq_correction && freq_correction[frequency]) || 0;
  const correctedTargetSPL = targetSPL - freqCorr; // subtract correction to compensate
  
  // SPL produced by headphone at max DAC output (full digital scale = 1.0):
  // Power at full scale = Vrms² / impedance (in watts), convert to mW
  const powerAtFullScale_mW = (dacVrms * dacVrms) / impedance * 1000;
  const splAtFullScale = sensitivity_db_mw + 10 * Math.log10(powerAtFullScale_mW);
  
  // Gain needed = 10^((targetSPL - splAtFullScale) / 20)
  const gain = Math.pow(10, (correctedTargetSPL - splAtFullScale) / 20);
  
  // Clamp to 0.0 - 1.0 (can't exceed full digital scale)
  return Math.max(0, Math.min(gain, 1.0));
}
